import type {
  ManagerView,
  TrackedEnvelope,
  TrackingQuery
} from "../types/tracking.types.ts"

/**
 * Each task view as the tracking list's own filters, so its rows and its count
 * come from the same scoped query as everything else on the screen. A view that
 * names a status replaces the status filter rather than intersecting with it,
 * since "Drafts" filtered to "Sent" can only ever be empty.
 */
export const queryForView = (
  view: ManagerView,
  base: TrackingQuery
): TrackingQuery => {
  const { tile: _tile, ...rest } = base
  switch (view) {
    case "drafts":
      return { ...rest, status: "draft" }
    case "mine":
      return { ...rest, tile: "awaiting_countersignature" }
    case "waiting":
      return { ...rest, tile: "out_for_signature" }
    case "completed":
      return { ...rest, status: "signed,completed" }
    default:
      return base
  }
}

export const viewNamesStatus = (view: ManagerView) =>
  view === "drafts" || view === "completed"

const OWES_NOTHING = ["signed", "declined", "expired"]

/** The signers the request is waiting on: those still owing at the lowest number. */
export const waitingOn = (envelope: TrackedEnvelope) => {
  const owing = envelope.recipients.filter(
    (recipient) =>
      recipient.role === "signer" && !OWES_NOTHING.includes(recipient.status)
  )
  if (!owing.length) return []
  const turn = Math.min(...owing.map((recipient) => recipient.signing_order))
  return owing.filter((recipient) => recipient.signing_order === turn)
}

export type RowAction = "resume" | "reviewAndSign" | "viewSigned" | "view"

/** The one thing a row offers first; everything else stays in its menu. */
export const primaryActionOf = (
  envelope: TrackedEnvelope,
  { canCountersign }: { canCountersign: boolean }
): RowAction => {
  if (envelope.status === "draft") return "resume"
  if (canCountersign && envelope.stage === "awaiting_countersignature")
    return "reviewAndSign"
  if (envelope.status === "signed" || envelope.status === "completed")
    return "viewSigned"
  return "view"
}
